<script lang="ts">
export default {
  name: 'BulkRemapPromptMappingModal',
};
</script>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import Alert from '@/components/v2/Alert.vue';
import Checkbox from '@/components/v2/Checkbox.vue';
import { AlertTriangle } from '@lucide/vue';
import type { AIPrompt } from '@/types/ai';
import { PROMPT_VERSION_LATEST } from '@/types/ai';
import { aiService } from '@/services/AIService';
import { useToast } from '@/composables/useToast';

const props = defineProps<{
  prompts: AIPrompt[];
}>();

const emit = defineEmits<{
  saved: [];
}>();

const toast = useToast();

const isOpen = ref(false);
const isLoading = ref(false);
const selectedIds = ref<number[]>([]);
const useLatest = ref(true);
const selectedPromptId = ref('');
const selectedVersion = ref('');

const publishedPromptIds = computed(() => {
  const ids = new Set<string>();
  for (const prompt of props.prompts) {
    if (!prompt.isDraft) ids.add(String(prompt.promptId));
  }
  return ids;
});

interface SelectOption {
  label: string;
  value: string;
}

const promptNameOptions = computed<SelectOption[]>(() => {
  const seen = new Set<string>();
  return props.prompts
    .filter((prompt) => {
      const id = String(prompt.promptId);
      if (seen.has(id) || !publishedPromptIds.value.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((prompt) => ({ label: prompt.name, value: String(prompt.promptId) }));
});

const versionOptions = computed<SelectOption[]>(() => {
  if (!selectedPromptId.value) return [];
  return props.prompts
    .filter((prompt) => String(prompt.promptId) === selectedPromptId.value && !prompt.isDraft)
    .map((prompt) => ({ label: `v${prompt.version}`, value: prompt.version }));
});

watch(selectedPromptId, () => {
  selectedVersion.value = '';
});

const isFormValid = computed(
  () => !!selectedPromptId.value && (useLatest.value || !!selectedVersion.value) && selectedIds.value.length > 0,
);

const show = (ids: number[]) => {
  selectedIds.value = [...ids];
  selectedPromptId.value = '';
  selectedVersion.value = '';
  useLatest.value = true;
  isLoading.value = false;
  isOpen.value = true;
};

const handleCancel = () => {
  if (isLoading.value) return;
  isOpen.value = false;
};

const handleAction = async () => {
  if (!isFormValid.value) return;
  isLoading.value = true;
  try {
    const response = await aiService.bulkUpdatePromptMappings({
      ids: selectedIds.value,
      promptId: Number(selectedPromptId.value),
      promptVersion: useLatest.value ? PROMPT_VERSION_LATEST : selectedVersion.value,
    });
    toast.success(`Remapped ${response.updatedCount} mapping(s)`);
    emit('saved');
    isOpen.value = false;
  } catch (error) {
    console.error('Failed to bulk update prompt mappings:', error);
    toast.error('Failed to remap selected mappings');
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ show });
</script>

<template>
  <AppModal
    :show="isOpen"
    title="Remap Selected Mappings"
    action-button-label="Remap"
    :handle-cancel="handleCancel"
    :handle-action="handleAction"
    :loading="isLoading"
    :action-disabled="!isFormValid || isLoading"
    show-overflow>
    <template #content>
      <div class="space-y-6">
        <Alert type="warning" :icon="AlertTriangle">
          This will override the prompt and version on
          <span class="font-semibold">{{ selectedIds.length }}</span>
          selected mapping{{ selectedIds.length === 1 ? '' : 's' }}. Coverage and document type will not change.
        </Alert>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-slate-700">
            Prompt <span class="text-red-500">*</span>
          </label>
          <AppSelect
            v-model="selectedPromptId"
            :options="promptNameOptions"
            placeholder="Select a prompt..."
            class="w-full" />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-slate-700">
            Version <span class="text-red-500">*</span>
          </label>
          <Checkbox
            v-model="useLatest"
            label="Latest (auto-updates when a new version is published)" />
          <AppSelect
            v-model="selectedVersion"
            :options="versionOptions"
            placeholder="Select a version..."
            :disabled="useLatest || !selectedPromptId"
            class="w-full" />
        </div>
      </div>
    </template>
  </AppModal>
</template>
